import { useMyCourses } from "@/hooks/use-my-courses";
import { pickCat } from "./CourseCard";

export default function MyProgressScreen() {
  const { data: courses = [], isLoading } = useMyCourses();

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center px-5">
        <p className="text-sm font-semibold text-[#8A6050]">Loading…</p>
      </div>
    );
  }

  const total = courses.length;
  const done = courses.filter((c) => c.completed || (c.progress ?? 0) >= 100).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const byCatMap = new Map<string, { done: number; total: number }>();
  for (const c of courses) {
    const k = c.category || "general";
    const entry = byCatMap.get(k) || { done: 0, total: 0 };
    entry.total += 1;
    if (c.completed || (c.progress ?? 0) >= 100) entry.done += 1;
    byCatMap.set(k, entry);
  }
  const byCat = Array.from(byCatMap.entries())
    .map(([category, v]) => ({ category, ...v }))
    .sort((a, b) => a.category.localeCompare(b.category));

  const R = 52;
  const CIRC = 2 * Math.PI * R;
  const dash = (pct / 100) * CIRC;

  return (
    <div className="min-h-screen bg-[#FBEAE0] px-5 pb-32 pt-12">
      <h1 className="text-2xl font-extrabold text-[#2A1810]">My progress</h1>

      {total === 0 ? (
        <p className="mt-10 text-center text-sm font-semibold text-[#8A6050]">
          No courses yet.
        </p>
      ) : (
        <>
          <div className="mt-6 flex flex-col items-center rounded-2xl border border-[#F0DDD2] bg-[#FFF6EC] p-6">
            <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
              <circle cx="70" cy="70" r={R} fill="none" stroke="#F0DDD2" strokeWidth="12" />
              <circle
                cx="70"
                cy="70"
                r={R}
                fill="none"
                stroke="#F44321"
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${CIRC}`}
              />
              <g transform="rotate(90 70 70)">
                <text
                  x="70"
                  y="68"
                  textAnchor="middle"
                  className="fill-[#2A1810]"
                  style={{ fontSize: 26, fontWeight: 800 }}
                >
                  {pct}%
                </text>
                <text
                  x="70"
                  y="88"
                  textAnchor="middle"
                  className="fill-[#8A6050]"
                  style={{ fontSize: 11, fontWeight: 700 }}
                >
                  complete
                </text>
              </g>
            </svg>
            <p className="mt-3 text-sm font-bold text-[#2A1810]">
              {done} of {total} courses done
            </p>
          </div>

          <div className="mt-5 space-y-3">
            {byCat.map((c) => {
              const p = c.total ? Math.round((c.done / c.total) * 100) : 0;
              const full = p >= 100;
              const col = full ? "#3B6D11" : "#F44321";
              const track = full ? "#EAF3DE" : "#F0DDD2";
              const cat = pickCat(c.category);
              return (
                <div
                  key={c.category}
                  className="rounded-2xl border border-[#F0DDD2] bg-[#FFF6EC] p-4"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="rounded-full px-2.5 py-0.5 text-[11px] font-bold capitalize"
                      style={{ background: cat.chipBg, color: cat.chipText }}
                    >
                      {c.category}
                    </span>
                    <span className="text-xs font-extrabold" style={{ color: col }}>
                      {p}% · {c.done}/{c.total}
                    </span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full" style={{ background: track }}>
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${p}%`, background: col }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
